import React from 'react';
import { graphql } from 'gatsby';
import { Link, useTranslation } from 'gatsby-plugin-react-i18next';
import { ArrowRight, BookOpen, Building2, MapPin } from 'lucide-react';
import FlexEZPageLayout from '@components/FlexEZPageLayout';
import ContactUsLink from '@components/ContactUsLink';

export { default as Head } from '@components/PageHead';

const CaseStudiesIndexPage = () => {
  const { t } = useTranslation('case-studies');

  const caseStudies = [
    {
      company: t('studies.alive.company'),
      title: t('studies.alive.title'),
      description: t('studies.alive.description'),
      location: t('studies.alive.location'),
      solution: t('studies.alive.solution'),
      result: t('studies.alive.result'),
      image: '/images/case-studies/alive-groupe/experience-center-tablet.jpg',
      imageAlt: t('studies.alive.imageAlt'),
      uri: '/flexez/case-studies/alive-groupe'
    }
  ];

  return (
    <FlexEZPageLayout>
      <section className="pt-32 pb-16 bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-sm font-semibold text-cyan-300 uppercase tracking-wide mb-4">
              {t('hero.eyebrow')}
            </div>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              {t('hero.title')}
            </h1>
            <p className="text-xl text-slate-300">
              {t('hero.description')}
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-3">
                {t('list.title')}
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl">
                {t('list.description')}
              </p>
            </div>
            <ContactUsLink>
              <button className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-cyan-700 text-white rounded-lg font-semibold hover:bg-cyan-800 transition-colors">
                <BookOpen size={20} />
                {t('list.cta')}
              </button>
            </ContactUsLink>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {caseStudies.map((study) => (
              <article
                key={study.company}
                className="border border-slate-200 rounded-lg overflow-hidden shadow-sm bg-white"
              >
                <img
                  src={study.image}
                  alt={study.imageAlt}
                  className="w-full h-72 object-cover"
                />
                <div className="p-7">
                  <div className="text-sm font-semibold text-cyan-700 uppercase tracking-wide mb-3">
                    {study.company}
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">
                    {study.title}
                  </h3>
                  <p className="text-slate-600 mb-6">
                    {study.description}
                  </p>

                  <div className="grid sm:grid-cols-2 gap-4 mb-6">
                    <div className="flex gap-3">
                      <MapPin className="text-cyan-700 flex-shrink-0 mt-1" size={20} />
                      <div>
                        <div className="text-sm text-slate-500">{t('labels.location')}</div>
                        <div className="font-semibold text-slate-900">{study.location}</div>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <Building2 className="text-cyan-700 flex-shrink-0 mt-1" size={20} />
                      <div>
                        <div className="text-sm text-slate-500">{t('labels.solution')}</div>
                        <div className="font-semibold text-slate-900">{study.solution}</div>
                      </div>
                    </div>
                  </div>

                  <div className="border-l-4 border-cyan-600 pl-4 mb-6">
                    <div className="text-sm text-slate-500 mb-1">{t('labels.result')}</div>
                    <p className="font-semibold text-slate-900">{study.result}</p>
                  </div>

                  <Link
                    to={study.uri}
                    className="inline-flex items-center gap-2 font-semibold text-cyan-700 hover:text-cyan-900"
                  >
                    {t('list.readStudy')}
                    <ArrowRight size={18} />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </FlexEZPageLayout>
  );
};

export default CaseStudiesIndexPage;

export const query = graphql`
  query($language: String!) {
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`;
